import { useCallback, useEffect, useRef } from "react";

import { Button, Checkbox, Icon_ShaprLogoRound, Spinner } from "@shapr3d/frontend-lib";

import { defaultProjectTitleName } from "../../config";
import useNavigation from "../../hooks/navigation";
import usePrevious from "../../hooks/usePrevious";
import { useProject } from "../../hooks/useProject";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { Modal } from "../../lib/modal";
import { useDeepLinkStateStore } from "../../stores/deepLinkStateStore";
import { truncateWithEllipsis } from "../../utils/truncateWithEllipsis";

import style from "./DeepLinkModal.module.css";

// The design intent is that the whole title should be at most 3 lines
const MAX_PROJECT_NAME_LENGTH = 61;

export const DeepLinkModal = () => {
  const tracker = useTracker();
  const navigation = useNavigation();
  const { t, m } = useTranslation();

  const { data } = useProject();
  const { isModalOpen, setIsModalOpen, isAutoOpenEnabled, setIsAutoOpenEnabled } =
    useDeepLinkStateStore();
  const wasAutoOpenRef = useRef(isModalOpen);

  const openDeepLink = useCallback(
    (autoOpen: boolean) => {
      if (!data) return;

      navigation.goToShaprApp({
        projectId: data.project.projectId,
        teamId: data.teamId,
        autoOpen,
      });
    },
    [data, navigation]
  );

  const isDeeplinkOpened = useRef(false);
  useEffect(() => {
    if (!data) return;

    if (isModalOpen) {
      if (!isDeeplinkOpened.current) {
        isDeeplinkOpened.current = true;
        openDeepLink(wasAutoOpenRef.current);
      }
    } else {
      isDeeplinkOpened.current = false;
      wasAutoOpenRef.current = false;
    }
  }, [isModalOpen, data, openDeepLink]);

  const previousIsModalOpen = usePrevious(isModalOpen);
  useEffect(() => {
    const projectId = data?.project.projectId;
    if (previousIsModalOpen !== undefined && projectId && previousIsModalOpen !== isModalOpen) {
      if (isModalOpen) {
        tracker.trackDeeplinkModalOpened({ projectId: data?.project.projectId });
      } else {
        tracker.trackDeeplinkModalClosed({ projectId: data?.project.projectId });
      }
    }
  }, [isModalOpen, previousIsModalOpen, data?.project.projectId, tracker]);

  return (
    <Modal telemetryName="deeplink" open={isModalOpen} onClose={() => setIsModalOpen(false)}>
      {!data && (
        <div className={style.spinnerContainer}>
          <Spinner />
        </div>
      )}
      {data && (
        <div className={style.container}>
          <div className={style.topContainer}>
            <Icon_ShaprLogoRound />
            <div className={style.title}>
              Opening{" "}
              <span className={style.projectName}>
                {`"${truncateWithEllipsis(
                  data?.project.name || defaultProjectTitleName,
                  MAX_PROJECT_NAME_LENGTH
                )}"`}
              </span>{" "}
              in Shapr3D
            </div>
            <div className={style.checkbox}>
              <Checkbox
                checked={isAutoOpenEnabled}
                onChange={(e) => {
                  tracker.trackDeeplinkAutoOpenToggled({
                    projectId: data.project.projectId,
                    autoOpen: e.target.checked,
                  });
                  setIsAutoOpenEnabled(e.target.checked);
                }}
                id="autoOpen"
              />
              <label className={style.checkboxLabel} htmlFor="autoOpen">
                Always open project links in the app
              </label>
            </div>
          </div>
          <div className={style.bottomContainer}>
            <Button
              onClick={() => {
                openDeepLink(false);
              }}
            >
              Launch Shapr3D
            </Button>
            <a
              href="https://www.shapr3d.com/download"
              target="_blank"
              rel="noreferrer"
              onClick={() => {
                tracker.trackDeeplinkGoToDownload({ projectId: data.project.projectId });
              }}
            >
              <Button variant="borderless">{t(m.General.DownloadShapr3D)}</Button>
            </a>
          </div>
        </div>
      )}
    </Modal>
  );
};
